import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { useDailyPledge } from "@/hooks/useDailyPledge";
import { Sun, Moon, CheckCircle, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const MOODS = [
  { emoji: "😊", label: "Bien" },
  { emoji: "😐", label: "Neutre" },
  { emoji: "😔", label: "Difficile" },
  { emoji: "😤", label: "Frustré·e" },
  { emoji: "😰", label: "Anxieux·se" },
  { emoji: "🥳", label: "Fier·e" },
];

interface Props {
  compact?: boolean;
}

export function DailyPledgeCard({ compact = false }: Props) {
  const {
    todayPledge,
    loading,
    submitMorningPledge,
    submitEveningCheckin,
    hasMorningPledge,
    hasEveningCheckin,
  } = useDailyPledge();
  const { toast } = useToast();

  const [pledgeText, setPledgeText] = useState("Aujourd'hui, je choisis de prendre soin de moi.");
  const [commitment, setCommitment] = useState([7]);
  const [mood, setMood] = useState("");
  const [cravingLevel, setCravingLevel] = useState([3]);
  const [eveningNotes, setEveningNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isEvening = new Date().getHours() >= 17;

  const handleMorningPledge = async () => {
    setSubmitting(true);
    const err = await submitMorningPledge(pledgeText, commitment[0]);
    setSubmitting(false);
    if (!err) {
      toast({ title: "Promesse enregistrée 💛", description: "Belle journée !" });
    }
  };

  const handleEveningCheckin = async () => {
    if (!mood) {
      toast({ title: "Choisis ton humeur", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const err = await submitEveningCheckin(mood, cravingLevel[0], eveningNotes);
    setSubmitting(false);
    if (!err) {
      toast({ title: "Bilan enregistré ✨", description: "Bravo pour cette journée !" });
    }
  };

  if (loading) return null;

  // Compact widget for Home
  if (compact) {
    return (
      <Card className="glass-card border-accent/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasMorningPledge ? (
                <CheckCircle className="w-4 h-4 text-success" />
              ) : (
                <Sun className="w-4 h-4 text-primary" />
              )}
              <span className="font-semibold text-sm">Promesse du jour</span>
            </div>
            {hasMorningPledge && hasEveningCheckin && (
              <Badge className="bg-success/15 text-success border-success/30 text-xs">
                ✓ Complété
              </Badge>
            )}
          </div>
          {!hasMorningPledge && (
            <p className="text-xs text-muted-foreground mt-2">
              Tu n'as pas encore fait ta promesse du jour.
            </p>
          )}
          {hasMorningPledge && !hasEveningCheckin && isEvening && (
            <p className="text-xs text-muted-foreground mt-2">
              Comment s'est passée ta journée ?
            </p>
          )}
          {hasMorningPledge && todayPledge?.morning_pledge_text && (
            <p className="text-xs italic text-foreground/70 mt-2 line-clamp-2">
              "{todayPledge.morning_pledge_text}"
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Morning pledge (not done yet)
  if (!hasMorningPledge) {
    return (
      <Card className="glass-card border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Sun className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Ma promesse du jour</CardTitle>
              <p className="text-xs text-muted-foreground">Quel engagement prends-tu aujourd'hui ?</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={pledgeText}
            onChange={(e) => setPledgeText(e.target.value)}
            placeholder="Aujourd'hui, je choisis de..."
            className="resize-none"
            rows={2}
          />
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Niveau d'engagement</span>
              <span className="font-bold text-primary">{commitment[0]}/10</span>
            </div>
            <Slider
              value={commitment}
              onValueChange={setCommitment}
              max={10}
              min={1}
              step={1}
              className="w-full"
            />
          </div>
          <Button
            onClick={handleMorningPledge}
            disabled={submitting || !pledgeText.trim()}
            className="w-full bg-primary text-primary-foreground hover:bg-primary-600"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {submitting ? "Enregistrement..." : "Faire ma promesse"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Evening check-in (pledge done, evening not done, it's evening)
  if (!hasEveningCheckin && isEvening) {
    return (
      <Card className="glass-card border-accent/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
              <Moon className="w-5 h-5 text-accent" />
            </div>
            <div>
              <CardTitle className="text-lg">Bilan du soir</CardTitle>
              <p className="text-xs text-muted-foreground">Comment s'est passée ta journée ?</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mood picker */}
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Ton humeur</span>
            <div className="flex gap-2 flex-wrap">
              {MOODS.map((m) => (
                <button
                  key={m.label}
                  onClick={() => setMood(m.label)}
                  className={`flex flex-col items-center gap-0.5 p-2 rounded-xl transition-all ${
                    mood === m.label
                      ? "bg-accent/15 ring-2 ring-accent/40 scale-110"
                      : "hover:bg-secondary"
                  }`}
                >
                  <span className="text-xl">{m.emoji}</span>
                  <span className="text-[10px] text-muted-foreground">{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Craving level */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Niveau d'envies</span>
              <span className="font-bold">{cravingLevel[0]}/10</span>
            </div>
            <Slider
              value={cravingLevel}
              onValueChange={setCravingLevel}
              max={10}
              min={0}
              step={1}
            />
          </div>

          <Textarea
            value={eveningNotes}
            onChange={(e) => setEveningNotes(e.target.value)}
            placeholder="Notes libres (optionnel)..."
            rows={2}
            className="resize-none"
          />

          <Button
            onClick={handleEveningCheckin}
            disabled={submitting}
            className="w-full"
          >
            {submitting ? "Enregistrement..." : "Enregistrer mon bilan"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // All done today
  return (
    <Card className="glass-card border-success/20">
      <CardContent className="p-6 text-center space-y-2">
        <CheckCircle className="w-10 h-10 text-success mx-auto" />
        <h3 className="font-bold text-lg">Journée complétée !</h3>
        <p className="text-sm text-muted-foreground">
          {todayPledge?.morning_pledge_text && (
            <span className="italic">"{todayPledge.morning_pledge_text}"</span>
          )}
        </p>
        {todayPledge?.evening_mood && (
          <Badge variant="outline" className="mt-2">
            Humeur : {todayPledge.evening_mood}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
